import React, { useState, useRef } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { Camera, Save, User, Mail, Phone, FileText, Lock, Eye, EyeOff } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';
import useSEO from '../hooks/useSEO';

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const { settings } = useSiteSettings();
  useSEO({ title: 'Profil Saya', siteName: settings.siteName, noindex: true });
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    bio: user?.bio || '',
  });
  const [pw, setPw] = useState({ current: '', newPw: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || null);

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Ukuran file maksimal 5MB'); return; }

    // Preview lokal
    setAvatarPreview(URL.createObjectURL(file));
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      const { data } = await API.post('/upload/avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateUser({ avatar: data.url });
      toast.success('Foto profil berhasil diperbarui');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal upload foto profil');
      setAvatarPreview(user?.avatar || null);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nama tidak boleh kosong'); return; }
    setSavingProfile(true);
    try {
      const { data } = await API.patch('/auth/me', {
        name: form.name.trim(),
        phone: form.phone.trim(),
        bio: form.bio.trim(),
      });
      updateUser(data.user);
      toast.success('Profil berhasil disimpan');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal menyimpan profil');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePw = async (e) => {
    e.preventDefault();
    if (pw.newPw.length < 6) { toast.error('Password baru minimal 6 karakter'); return; }
    if (pw.newPw !== pw.confirm) { toast.error('Konfirmasi password tidak cocok'); return; }
    setSavingPw(true);
    try {
      await API.patch('/auth/change-password', {
        currentPassword: pw.current,
        newPassword: pw.newPw,
      });
      toast.success('Password berhasil diubah');
      setPw({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal mengubah password');
    } finally {
      setSavingPw(false);
    }
  };

  const roleLabel = { reader: 'Reader', editor: 'Editor', producer: 'Producer', admin: 'Admin' };

  return (
    <DashboardLayout title="Profil Saya" subtitle="Kelola informasi akun dan keamanan">
      <div style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* ── Avatar + info dasar ── */}
        <div className="ki-card" style={{ padding: '28px 32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 28 }}>
            {/* Avatar */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              {avatarPreview
                ? <img src={avatarPreview} alt="Avatar"
                    style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--ki-border)' }} />
                : <div style={{
                    width: 80, height: 80, borderRadius: '50%',
                    background: 'var(--in-blue)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: 28, fontWeight: 700, color: '#fff',
                    border: '3px solid var(--ki-border)',
                  }}>{user?.name?.[0]?.toUpperCase()}</div>
              }
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploadingAvatar}
                style={{
                  position: 'absolute', bottom: 0, right: 0,
                  width: 26, height: 26, borderRadius: '50%',
                  background: 'var(--in-orange)', border: '2px solid #fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', padding: 0,
                }}
              >
                <Camera size={12} color="#fff" />
              </button>
              <input
                ref={fileRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp"
                style={{ display: 'none' }} onChange={handleAvatarChange}
              />
            </div>

            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ki-text)', marginBottom: 4 }}>{user?.name}</div>
              <div style={{ fontSize: 13, color: 'var(--ki-text-muted)', marginBottom: 6 }}>{user?.email}</div>
              <span style={{
                display: 'inline-block', padding: '2px 10px', borderRadius: 100,
                background: 'rgba(0,21,64,0.08)', fontSize: 11, fontWeight: 700,
                color: 'var(--in-blue)', textTransform: 'capitalize',
              }}>{roleLabel[user?.role] || user?.role}</span>
            </div>
          </div>

          <form onSubmit={handleSaveProfile}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">
                  <User size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />Nama Lengkap
                </label>
                <input
                  className="form-input"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Nama lengkap"
                  required
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">
                  <Mail size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />Email
                </label>
                <input
                  className="form-input"
                  value={user?.email || ''}
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">
                <Phone size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />Nomor Telepon
              </label>
              <input
                className="form-input"
                value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="Nomor telepon (opsional)"
              />
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">
                <FileText size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />Bio
              </label>
              <textarea
                className="form-input"
                value={form.bio}
                onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
                placeholder="Ceritakan sedikit tentang diri Anda (opsional)"
                rows={3}
                style={{ resize: 'vertical', minHeight: 80 }}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={savingProfile}>
              <Save size={14} /> {savingProfile ? 'Menyimpan...' : 'Simpan Profil'}
            </button>
          </form>
        </div>

        {/* ── Ganti password — hanya untuk akun local ── */}
        {user?.authProvider !== 'google' && (
          <div className="ki-card" style={{ padding: '28px 32px' }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 20, color: 'var(--ki-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Lock size={16} /> Ganti Password
            </div>
            <form onSubmit={handleChangePw}>
              <div className="form-group">
                <label className="form-label">Password Saat Ini</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="form-input"
                    type={showPw ? 'text' : 'password'}
                    value={pw.current}
                    onChange={e => setPw(p => ({ ...p, current: e.target.value }))}
                    placeholder="Password saat ini"
                    required
                    style={{ paddingRight: 40 }}
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ki-text-faint)', padding: 0 }}>
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Password Baru</label>
                  <input
                    className="form-input"
                    type="password"
                    value={pw.newPw}
                    onChange={e => setPw(p => ({ ...p, newPw: e.target.value }))}
                    placeholder="Min. 6 karakter"
                    required
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Konfirmasi Password Baru</label>
                  <input
                    className="form-input"
                    type="password"
                    value={pw.confirm}
                    onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))}
                    placeholder="Ulangi password baru"
                    required
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-secondary" disabled={savingPw} style={{ marginTop: 20 }}>
                <Lock size={14} /> {savingPw ? 'Menyimpan...' : 'Ganti Password'}
              </button>
            </form>
          </div>
        )}

        {/* Info akun Google */}
        {user?.authProvider === 'google' && (
          <div className="ki-card" style={{ padding: '20px 32px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 22 }}>🔒</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ki-text)', marginBottom: 2 }}>Login via Google</div>
              <div style={{ fontSize: 13, color: 'var(--ki-text-muted)' }}>Password dikelola oleh Google. Untuk mengubah password, lakukan di akun Google Anda.</div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
